import { jsPDF } from 'jspdf';

/**
 * Utility functions for generic operations
 * @namespace Util.Misc
 */

import { sortWorker } from './workers';

/**
 * Asynchrous sort function leveraging web workers
 * @function doSort
 * @memberof Util.Misc
 * @param {array} rows
 * @param {string} key
 * @param {boolean} reverse
 * @param {function} callback
 */
function asyncSort (rows = [], key, reverse=false, callback = () => {}) {
  if (!key) return callback(rows);
  sortWorker.postMessage({rows, key, reverse});
  sortWorker.onmessage = function (event) {
    callback(event.data);
  }
}

/**
 * Synchronous sort function for arrays of objects
 * @function simpleSort
 * @memberof Util.Misc
 * @param {array} rows
 * @param {string} key
 * @param {boolean} reverse
 */
function simpleSort (rows = [], key, reverse=false) {
  const sortedRows = rows.sort((a,b) => {
    if (!key) return;
    let value = 0;
    if (a[key] > b[key]) value = 1;
    if (a[key] < b[key]) value = -1;
    if (reverse) value = -value;
    return value;
  });
  return sortedRows;
}

/**
 * PDF generation and print helper function
 * @function printAsPDF
 * @memberof Util.Misc
 * @param {string} content
 * @param {string} filename
 */
function printAsPDF (content, filename = 'document') {
  // Create a new PDF: 'p' = portrait, 'pt' = points, 'letter' = 612x792
  const doc = new jsPDF('p', 'pt', 'letter');

  // Use the provided filename as PDF metadata so the argument has an effect.
  if (typeof doc.setProperties === 'function') {
    doc.setProperties({ title: filename });
  } else if (typeof doc.setDocumentProperties === 'function') {
    doc.setDocumentProperties({ title: filename });
  }

  // Add text to the PDF, wrapping and paginating so long content is not clipped.
  const marginX = 40;
  const marginY = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - (marginX * 2);
  const lines = doc.splitTextToSize(content || '', maxWidth);
  const lineHeight = doc.getFontSize() * 1.2;
  const maxY = pageHeight - marginY;
  let y = marginY;

  lines.forEach((line, index) => {
    if (index > 0 && (y + lineHeight) > maxY) {
      doc.addPage();
      y = marginY;
    }

    doc.text(line, marginX, y);
    y += lineHeight;
  });

  // Render PDF to a blob URL and print via a hidden iframe.
  const blob = doc.output('blob');
  const blobUrl = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  // Keep the iframe offscreen instead of display:none so print stays stable across browsers.
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.src = blobUrl;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      URL.revokeObjectURL(blobUrl);
      iframe.remove();
      return;
    }

    const cleanup = () => {
      URL.revokeObjectURL(blobUrl);
      iframe.remove();
    };

    // Cleanup once the user closes the print dialog.
    printWindow.onafterprint = cleanup;

    // Give the embedded PDF viewer a moment to initialize before printing.
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);

    // Fallback cleanup in case onafterprint is not fired.
    setTimeout(cleanup, 15000);
  };
}

export { asyncSort, simpleSort, printAsPDF };
